import React, { useEffect, useState } from "react";
import { ButtonSave } from "components/buttons";
import { ListingTable } from "components/listing";
import {
    AlertUnsavedData,
    NotificationToaster,
} from "components/notifications";
import { useDirtyData } from "hooks/useDirtyData";
import { Intent } from "@blueprintjs/core";
import axios from "axios";
import { LicenceCheckingService } from "services";
import {
    FormSwitch,
    FormNumericInput,
    FormMultiSelect,
} from "components/form-fields";

export function GreyFleetPolicy() {
    const badRequestCode = 400;
    const headers = ["rule", "parameters", "enabled"];
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [greyFleetOptions, setGreyFleetOptions] = useState([]);
    const [originalGreyFleetPolicy, setOriginalGreyFleetPolicy] = useState([]);
    const [greyFleetPolicy, setGreyFleetPolicy] = useState([]);
    const [tableRows, setTableRows] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);

    const isDataDirty = useDirtyData(originalGreyFleetPolicy, greyFleetPolicy);

    useEffect(initialLoad, []);

    function initialLoad() {
        setIsLoading(true);
        axios
            .all([
                LicenceCheckingService.getLicenceCheckingOptions(),
                LicenceCheckingService.getGreyFleetPolicy(),
            ])
            .then(
                axios.spread(function (options, policy) {
                    setGreyFleetOptions(options.fleetCheckPolicyRules);
                    setOriginalGreyFleetPolicy(policy);
                    setGreyFleetPolicy(policy);
                }),
                () => {
                    NotificationToaster.show(
                        Intent.DANGER,
                        "Could not load grey fleet vehicle policy. Please refresh the page."
                    );
                }
            )
            .catch(() => {
                NotificationToaster.show(
                    Intent.DANGER,
                    "Could not load grey fleet vehicle policy. Please refresh the page."
                );
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    useEffect(() => {
        if (greyFleetOptions.length === 0) {
            return;
        }

        setTableRows(
            greyFleetOptions.map((option) => {
                const valueFromPolicy = getPolicyValues(option.key);

                return [
                    <p key={`${option.key}-grey-fleet-policy-name`}>
                        {option.name}
                    </p>,
                    <div key={`${option.key}-grey-fleet-policy-value`}>
                        {renderParameterField(option, valueFromPolicy)}
                    </div>,
                    <FormSwitch
                        key={`${option.key}-grey-fleet-policy-enabled`}
                        id={`enabled-${option.id}`}
                        checked={valueFromPolicy !== undefined}
                        onChange={(e) =>
                            enableOrDisablePolicyRule(option, e.target.checked)
                        }
                    />,
                ];
            })
        );

        setTotalRecords(greyFleetOptions.length);
    }, [greyFleetOptions, greyFleetPolicy]);

    function renderParameterField(option, values) {
        switch (option.ruleType) {
            case "NUMBER":
                return (
                    <FormNumericInput
                        min={option.min}
                        max={option.max}
                        selectedValue={
                            Array.isArray(values) && values.length > 0
                                ? values[0]
                                : 0
                        }
                        onValueChange={(newValue) =>
                            handleOptionValueChange(option, newValue)
                        }
                        disabled={values === undefined}
                        disabledInput={values === undefined}
                        unit={option.suffix}
                    />
                );
            case "MULTISELECT":
                return (
                    <FormMultiSelect
                        disabled={isSaving || values === undefined}
                        items={option.options}
                        onItemSelect={(item) =>
                            handleOptionValueChange(option, item.id)
                        }
                        onRemove={(item) =>
                            handleOptionValueChange(option, item, true)
                        }
                        selectedValues={
                            Array.isArray(values)
                                ? values.map((id) => ({ id }))
                                : []
                        }
                    />
                );
            default:
                return <span>-</span>;
        }
    }

    function getPolicyValues(optionKey) {
        const policyObj = greyFleetPolicy.find(
            (p) => p.policyRuleKey === optionKey
        );
        return policyObj ? policyObj.values : undefined;
    }

    function handleOptionValueChange(option, newValue, remove = false) {
        setGreyFleetPolicy((prevPolicy) => {
            const existingPolicy = prevPolicy.find(
                (p) => p.policyRuleKey === option.key
            );
            if (!existingPolicy) {
                return prevPolicy;
            }

            if (option.key === "FUELTYPE") {
                let updatedValues = existingPolicy.values;
                if (remove) {
                    const match = option.options.find(
                        (opt) => opt.name === newValue
                    );
                    const removeId = match ? match.id : null;
                    if (removeId !== null) {
                        updatedValues = updatedValues.filter(
                            (v) => v !== removeId
                        );
                    }
                } else if (!updatedValues.includes(newValue)) {
                    updatedValues = [...updatedValues, newValue];
                }
                return prevPolicy.map((p) =>
                    p.policyRuleKey === option.key
                        ? { ...p, values: updatedValues }
                        : p
                );
            } else {
                return prevPolicy.map((p) =>
                    p.policyRuleKey === option.key
                        ? { ...p, values: [newValue] }
                        : p
                );
            }
        });
    }

    function saveChanges() {
        setIsSaving(true);
        LicenceCheckingService.saveGreyFleetPolicy(greyFleetPolicy)
            .then(
                () => {
                    NotificationToaster.show(
                        Intent.SUCCESS,
                        "Grey Fleet vehicle policy saved successfully."
                    );
                    setOriginalGreyFleetPolicy(greyFleetPolicy);
                },
                (error) => {
                    if (error.response.status === badRequestCode) {
                        NotificationToaster.show(
                            Intent.WARNING,
                            error.response.data
                        );
                    } else {
                        NotificationToaster.show(
                            Intent.DANGER,
                            "Failed to save Grey Fleet vehicle policy. Please try again."
                        );
                    }
                }
            )
            .catch(() => {
                NotificationToaster.show(
                    Intent.DANGER,
                    "Failed to save Grey Fleet vehicle policy. Please try again."
                );
            })
            .finally(() => {
                setIsSaving(false);
            });
    }

    function enableOrDisablePolicyRule(option, checked) {
        setGreyFleetPolicy((prevPolicy) =>
            checked
                ? [
                      ...prevPolicy,
                      {
                          policyRuleKey: option.key,
                          values: [],
                      },
                  ]
                : prevPolicy.filter((p) => p.policyRuleKey !== option.key)
        );
    }

    return (
        <div className="row">
            <h2>Policy</h2>

            <p>
                Set the rules and their parameters to match your company's grey
                fleet policy
            </p>

            <ListingTable
                id="grey-fleet-vehicle-policy-table"
                headers={headers}
                data={tableRows}
                totalRecordCount={totalRecords}
                loadingData={isLoading}
                showPerPage={false}
                pageable={false}
            />

            <div className="spacer-top">
                <ButtonSave
                    onClick={saveChanges}
                    disabled={isSaving}
                    simpleDisabled={!isDataDirty}
                    loading={isLoading}
                ></ButtonSave>
            </div>

            <AlertUnsavedData
                isDirty={isDataDirty}
                scrollToBottomOnCancel={true}
            />
        </div>
    );
}
